// worldCompass — which way is out.
//
// Khari, 2026-08-03: "eventually, we will be plotting our worlds
// programatically, so it will be crucially important to have this world compass
// in place."
//
// This file exists because I got a placement backwards and could not tell from
// the code which way was wrong. I reasoned from the street cameras — every
// venue's camera stands at a LOWER z than its building and looks toward it, so
// the approach must be −z — put the studio's doorman there, and drove him
// through the back wall. The concert hall's guard, placed by eye and known
// good, stands at +z.
//
// Both facts are true. The camera geometry describes where you STAND to look at
// a venue; the venue anchors come from the city MODEL's own geometry, and the
// model's storefronts face +z. Nothing wrote that down, so each of us knew half
// of it and neither half was labelled.
//
// A world you plot by hand can survive that. A world you plot PROGRAMMATICALLY
// cannot: "put a doorman outside the door" has to be a function, and a function
// needs a convention. This is the convention.

// ── THE CONVENTION ───────────────────────────────────────────────────────────
//
//   +z   OUT of a building, toward the street. The storefront side. Where a
//        doorman stands, where a queue forms, where a sign faces.
//   −z   INTO a building. Behind the facade. Anything placed here is indoors,
//        which is exactly the bug this file was written after.
//   +x   right, as you stand in the street facing the building's front
//   +y   up
//
// rotationY 0 faces +z — a body at rot 0 looks out at the street, which is what
// a doorman does. Math.PI turns him around to face the door.
export const OUT = 1;            // multiply a distance by this to move streetward
export const FACING_STREET = 0;  // rotationY for a body looking out at the approach
export const FACING_DOOR = Math.PI;

/**
 * A spot in front of a venue.
 *
 * @param {number[]} anchor  the venue anchor, [x, y, z] from venueAnchors
 * @param {object}   opts
 *   distance  metres out toward the street (default 5 — clear of the facade)
 *   across    metres along the storefront, + is right as you face the building
 *   lift      metres above the anchor's ground
 * @returns {number[]} [x, y, z] world position
 *
 * Use this rather than adding offsets by hand. Every caller that does its own
 * arithmetic is a caller that can get the sign wrong, and getting the sign wrong
 * is silent — the body renders happily, just inside a wall where nobody looks.
 */
export function frontOf(anchor, { distance = 5, across = 0, lift = 0 } = {}) {
  if (!Array.isArray(anchor)) return [0, 0, 0];
  const [ax, ay = 0, az] = anchor;
  return [ax + across, (ay || 0) + lift, az + OUT * distance];
}

/** True when a position sits on the street side of a venue rather than inside it. */
export function isOutside(anchor, pos) {
  if (!Array.isArray(anchor) || !Array.isArray(pos)) return false;
  return (pos[2] - anchor[2]) * OUT > 0;
}
